import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { WarehouseService } from 'src/app/warehouse/warehouse.service';
import {
  ProviderWarehouse,
  ProviderWarehouseDelivery,
  ProviderWarehouseDeliveryMethod,
  Warehouse
} from 'src/typings';
import {
  deliveryEtaLine,
  getPickupRules,
  summarizeDelivery
} from '../provider-warehouse-delivery.util';
import { ProviderWarehouseService } from '../provider-warehouse.service';
import { initialReceiverWarehouseId } from '../provider-warehouse-route.util';

interface OwnRuleDraft {
  arrivalDays: number[];
  days: number[];
  cutoff: string;
  arrivalDayOffset: number;
  arrivalFrom: string;
  arrivalTo: string;
}

interface CarrierRuleDraft {
  days: number[];
  cutoff: string;
  shipDayOffset: number;
  transitDays: number | null;
}

interface RunsRuleDraft {
  cutoff: string;
  arrivalFrom: string;
  arrivalTo: string;
  arrivalDayOffset: number;
  days: number[];
  departures: string[];
  transitMinutes: number | null;
}

interface PickupRuleDraft {
  days: number[];
  from: string;
  to: string;
}

const WORKDAYS = [1, 2, 3, 4, 5];
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

function settle<T>(
  promise: Promise<T>
): Promise<{ status: 'fulfilled'; value: T } | { status: 'rejected'; reason: unknown }> {
  return promise.then(
    (value) => ({ status: 'fulfilled', value }),
    (reason) => ({ status: 'rejected', reason })
  );
}

@Component({
  selector: 'app-warehouse-delivery',
  templateUrl: './warehouse-delivery.component.html',
  styleUrls: ['./warehouse-delivery.component.scss']
})
export class WarehouseDeliveryComponent implements OnInit, OnDestroy {
  wh: ProviderWarehouse | undefined;

  receiverWarehouses: Warehouse[] = [];
  receiverWarehouseId: number | null = null;
  loading = true;
  saving = false;
  switching = false;
  loadError = '';
  private pairReady = false;
  private savedDraft = '';
  private requestedProviderWarehouseId: number | null = null;
  private requestedReceiverWarehouseId: number | null = null;
  private destroyed = false;
  private destroy$ = new Subject<void>();

  @ViewChild('unsavedDialog', { static: true }) unsavedDialog!: TemplateRef<unknown>;

  get receiverWarehouse() {
    return this.receiverWarehouses.find((warehouse) => warehouse.id === this.receiverWarehouseId);
  }

  get canEdit() {
    return this.pairReady && !this.loading && !this.saving && !this.switching;
  }

  get hasUnsavedChanges() {
    return this.pairReady && this.draftSnapshot() !== this.savedDraft;
  }

  get backQueryParams() {
    return { receiverWarehouseId: this.receiverWarehouseId };
  }

  weekdays = [
    { value: 1, label: 'Пн' },
    { value: 2, label: 'Вт' },
    { value: 3, label: 'Ср' },
    { value: 4, label: 'Чт' },
    { value: 5, label: 'Пт' },
    { value: 6, label: 'Сб' },
    { value: 7, label: 'Вс' }
  ];

  arrivalOffsets = [
    { value: 0, label: 'в тот же день' },
    { value: 1, label: 'на след. день' },
    { value: 2, label: 'через 2 дня' },
    { value: 3, label: 'через 3 дня' }
  ];

  ownEnabled = false;

  ownRules: OwnRuleDraft[] = [];

  carrierEnabled = false;

  carrierName = '';

  carrierRules: CarrierRuleDraft[] = [];

  runsEnabled = false;

  runsRules: RunsRuleDraft[] = [];

  pickupEnabled = false;
  pickupTravelMinutes: number | null = null;
  pickupHandlingMinutes: number | null = null;
  pickupDistanceKm: number | null = null;
  pickupAddress = '';
  copyReceiverId: number | null = null;

  pickupRules: PickupRuleDraft[] = [];

  deliveryNote = '';

  get deliveryPreview(): string[] {
    return summarizeDelivery(this.buildDeliveryPayload());
  }

  get deliveryEtaPreview(): string {
    return deliveryEtaLine(this.buildDeliveryPayload(), new Date());
  }

  get backLink() {
    return this.wh?.provider ? ['/provider/profile', this.wh.provider.id] : ['/provider'];
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private providerWarehouseService: ProviderWarehouseService,
    private notificationService: NotificationService,
    private warehouseService: WarehouseService,
    private store: Store<AppState>,
    private dialog: MatDialog
  ) {}

  async ngOnInit() {
    try {
      const [warehouses, user] = await Promise.all([
        firstValueFrom(this.warehouseService.getAllList().pipe(takeUntil(this.destroy$))),
        firstValueFrom(
          this.store
            .select((state) => state.user.info)
            .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
        )
      ]);
      if (this.destroyed) return;
      this.receiverWarehouses = warehouses;
      this.loading = false;
      combineLatest([this.activatedRoute.paramMap, this.activatedRoute.queryParamMap])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([params, query]) => {
          const receiverId = initialReceiverWarehouseId(
            warehouses,
            query.get('receiverWarehouseId'),
            user?.defaultWarehouse?.id
          );
          void this.changeContext(Number(params.get('id')), receiverId);
        });
    } catch {
      if (this.destroyed) return;
      this.loading = false;
      this.loadError = 'Не удалось загрузить склады. Обновите страницу, чтобы повторить.';
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async changeContext(
    providerWarehouseId: number,
    receiverId: number | null,
    force = false
  ) {
    if (!providerWarehouseId) return;
    if (!force && this.wh?.id === providerWarehouseId && this.receiverWarehouseId === receiverId) {
      return;
    }
    if (this.loading || this.saving || this.switching) {
      await this.syncRoute();
      return;
    }
    this.switching = true;
    try {
      if (this.hasUnsavedChanges) {
        const choice = await firstValueFrom(
          this.dialog.open(this.unsavedDialog, { width: '460px', disableClose: true }).afterClosed()
        );
        if (this.destroyed) return;
        if (choice !== 'discard' && (choice !== 'save' || !(await this.save()))) {
          await this.syncRoute();
          return;
        }
      }
      this.loading = true;
      this.pairReady = false;
      this.requestedProviderWarehouseId = providerWarehouseId;
      this.requestedReceiverWarehouseId = receiverId;
      this.receiverWarehouseId = receiverId;
      this.loadError = '';
      this.loadDeliveryDraft(null);
      const [warehouseResult, routeResult] = await Promise.all([
        settle(
          this.wh?.id === providerWarehouseId
            ? Promise.resolve(this.wh)
            : firstValueFrom(this.providerWarehouseService.getOne(providerWarehouseId))
        ),
        settle(
          receiverId
            ? firstValueFrom(
                this.providerWarehouseService.getDelivery(providerWarehouseId, receiverId)
              )
            : Promise.resolve(null)
        )
      ]);
      if (this.destroyed) return;
      if (warehouseResult.status === 'rejected') throw warehouseResult.reason;
      this.wh = warehouseResult.value;
      if (routeResult.status === 'rejected') throw routeResult.reason;
      const route = routeResult.value;
      this.loadDeliveryDraft(route?.delivery);
      this.pairReady = Boolean(route);
      this.savedDraft = this.draftSnapshot();
    } catch {
      if (!this.destroyed) {
        this.loadError = 'Не удалось загрузить доставку для выбранного склада. Повторите загрузку.';
      }
    } finally {
      if (this.wh?.id === providerWarehouseId) await this.syncRoute();
      this.loading = false;
      this.switching = false;
    }
  }

  async retryLoad() {
    if (this.requestedProviderWarehouseId) {
      await this.changeContext(
        this.requestedProviderWarehouseId,
        this.requestedReceiverWarehouseId,
        true
      );
    }
  }

  private async syncRoute() {
    if (!this.wh || this.destroyed) return;
    if (
      Number(this.activatedRoute.snapshot.paramMap.get('id')) === this.wh.id &&
      this.activatedRoute.snapshot.queryParamMap.get('receiverWarehouseId') ===
        (this.receiverWarehouseId === null ? null : String(this.receiverWarehouseId))
    )
      return;
    await this.router.navigate(['/provider/warehouse', this.wh.id, 'delivery'], {
      queryParams: this.backQueryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private draftSnapshot() {
    return JSON.stringify({
      ownEnabled: this.ownEnabled,
      ownRules: this.ownRules,
      carrierEnabled: this.carrierEnabled,
      carrierName: this.carrierName,
      carrierRules: this.carrierRules,
      runsEnabled: this.runsEnabled,
      runsRules: this.runsRules,
      pickupEnabled: this.pickupEnabled,
      pickupRules: this.pickupRules,
      pickupTravelMinutes: this.pickupTravelMinutes,
      pickupHandlingMinutes: this.pickupHandlingMinutes,
      pickupDistanceKm: this.pickupDistanceKm,
      pickupAddress: this.pickupAddress,
      deliveryNote: this.deliveryNote
    });
  }

  toggleOwn(enabled: boolean) {
    this.ownEnabled = enabled;
    if (enabled && !this.ownRules.length) this.addOwnRule();
  }

  addOwnRule() {
    this.ownRules.push({
      days: this.nextRuleDays(this.ownRules),
      cutoff: '17:00',
      arrivalDayOffset: 1,
      arrivalDays: [],
      arrivalFrom: '',
      arrivalTo: ''
    });
  }

  removeOwnRule(index: number) {
    this.ownRules.splice(index, 1);
  }

  toggleCarrier(enabled: boolean) {
    this.carrierEnabled = enabled;
    if (enabled && !this.carrierRules.length) {
      if (!this.carrierName) this.carrierName = 'Новая почта';
      this.addCarrierRule();
    }
  }

  addCarrierRule() {
    this.carrierRules.push({
      days: this.nextRuleDays(this.carrierRules),
      cutoff: '16:00',
      shipDayOffset: 0,
      transitDays: 1
    });
  }

  removeCarrierRule(index: number) {
    this.carrierRules.splice(index, 1);
  }

  toggleRuns(enabled: boolean) {
    this.runsEnabled = enabled;
    if (enabled && !this.runsRules.length) this.addRunsRule();
  }

  addRunsRule() {
    this.runsRules.push({
      days: [...WORKDAYS],
      departures: ['09:00'],
      cutoff: '08:30',
      arrivalFrom: '',
      arrivalTo: '',
      arrivalDayOffset: 0,
      transitMinutes: 60
    });
  }

  removeRunsRule(index: number) {
    this.runsRules.splice(index, 1);
  }

  addDeparture(rule: RunsRuleDraft) {
    rule.departures.push('');
  }

  removeDeparture(rule: RunsRuleDraft, index: number) {
    rule.departures.splice(index, 1);
  }

  togglePickup(enabled: boolean) {
    this.pickupEnabled = enabled;
    if (enabled && !this.pickupRules.length) this.addPickupRule();
  }

  addPickupRule() {
    this.pickupRules.push({
      days: this.pickupRules.length ? this.nextRuleDays(this.pickupRules) : [...WORKDAYS],
      from: '09:00',
      to: '18:00'
    });
  }

  removePickupRule(index: number) {
    this.pickupRules.splice(index, 1);
  }

  trackByIndex(index: number) {
    return index;
  }

  // Для нового правила предлагаем дни, ещё не занятые другими правилами способа
  // (первое правило — будни).
  private nextRuleDays(rules: Array<{ days: number[] }>): number[] {
    if (!rules.length) return [...WORKDAYS];
    const used = new Set<number>();
    rules.forEach((rule) => rule.days.forEach((day) => used.add(day)));
    return ALL_DAYS.filter((day) => !used.has(day));
  }

  duplicateDaysLabel(rules: Array<{ days: number[] }>): string {
    const seen = new Set<number>();
    const duplicates = new Set<number>();
    rules.forEach((rule) =>
      rule.days.forEach((day) => (seen.has(day) ? duplicates.add(day) : seen.add(day)))
    );
    if (!duplicates.size) return '';
    const labels = this.weekdays
      .filter((weekday) => duplicates.has(weekday.value))
      .map((weekday) => weekday.label);
    return `${labels.join(', ')} — в нескольких правилах`;
  }

  private loadDeliveryDraft(delivery?: ProviderWarehouseDelivery | null) {
    this.ownEnabled = false;
    this.ownRules = [];
    this.carrierEnabled = false;
    this.carrierName = '';
    this.carrierRules = [];
    this.runsEnabled = false;
    this.runsRules = [];
    this.pickupEnabled = false;
    this.pickupRules = [];
    this.pickupTravelMinutes = null;
    this.pickupHandlingMinutes = null;
    this.pickupDistanceKm = null;
    this.pickupAddress = '';
    this.deliveryNote = delivery?.note || '';

    (delivery?.methods || []).forEach((method) => {
      switch (method.type) {
        case 'own':
          this.ownEnabled = true;
          this.ownRules = method.rules.map((rule) => ({
            days: [...rule.days],
            cutoff: rule.cutoff,
            arrivalDayOffset: rule.arrivalDayOffset,
            arrivalDays: [...(rule.arrivalDays || [])],
            arrivalFrom: rule.arrivalFrom || '',
            arrivalTo: rule.arrivalTo || ''
          }));
          break;
        case 'carrier':
          this.carrierEnabled = true;
          this.carrierName = method.carrier || '';
          this.carrierRules = method.rules.map((rule) => ({
            days: [...rule.days],
            cutoff: rule.cutoff,
            shipDayOffset: rule.shipDayOffset || 0,
            transitDays: rule.transitDays ?? null
          }));
          break;
        case 'runs':
          this.runsEnabled = true;
          this.runsRules = method.rules.reduce<RunsRuleDraft[]>(
            (rows, rule) =>
              rows.concat(
                rule.departures.map((departure) => ({
                  days: [...rule.days],
                  departures: [departure],
                  cutoff: rule.cutoff || '',
                  arrivalFrom: rule.arrivalFrom || '',
                  arrivalTo: rule.arrivalTo || '',
                  arrivalDayOffset: rule.arrivalDayOffset || 0,
                  transitMinutes: rule.transitMinutes ?? null
                }))
              ),
            []
          );
          break;
        case 'pickup':
          this.pickupEnabled = true;
          this.pickupTravelMinutes = method.travelMinutes ?? null;
          this.pickupHandlingMinutes = method.handlingMinutes ?? null;
          this.pickupDistanceKm = method.distanceKm ?? null;
          this.pickupAddress = method.address || '';
          this.pickupRules = getPickupRules(method).map((rule) => ({
            days: [...(rule.days || [])],
            from: rule.from || '',
            to: rule.to || ''
          }));
          break;
      }
    });
  }

  private buildDeliveryPayload(): ProviderWarehouseDelivery | null {
    const methods: ProviderWarehouseDeliveryMethod[] = [];

    if (this.ownEnabled) {
      const rules = this.ownRules
        .filter((rule) => rule.days.length && rule.cutoff)
        .map((rule) => ({
          days: [...rule.days].sort(),
          cutoff: rule.cutoff,
          arrivalDayOffset: Number(rule.arrivalDayOffset) || 0,
          ...(rule.arrivalDays?.length ? { arrivalDays: [...rule.arrivalDays] } : {}),
          arrivalFrom: rule.arrivalFrom || undefined,
          arrivalTo: rule.arrivalTo || undefined
        }));
      if (rules.length) methods.push({ type: 'own', rules });
    }

    if (this.carrierEnabled) {
      const rules = this.carrierRules
        .filter((rule) => rule.days.length && rule.cutoff)
        .map((rule) => ({
          days: [...rule.days].sort(),
          cutoff: rule.cutoff,
          shipDayOffset: Number(rule.shipDayOffset) || 0,
          transitDays: rule.transitDays ? Number(rule.transitDays) : undefined
        }));
      if (rules.length) {
        methods.push({ type: 'carrier', carrier: this.carrierName.trim() || undefined, rules });
      }
    }

    if (this.runsEnabled) {
      const rules = this.runsRules
        .map((rule) => ({
          days: [...rule.days].sort(),
          departures: rule.departures.filter(Boolean).sort(),
          ...(rule.cutoff ? { cutoff: rule.cutoff } : {}),
          ...(rule.arrivalFrom ? { arrivalFrom: rule.arrivalFrom } : {}),
          ...(rule.arrivalTo ? { arrivalTo: rule.arrivalTo } : {}),
          ...(rule.arrivalDayOffset ? { arrivalDayOffset: Number(rule.arrivalDayOffset) } : {}),
          transitMinutes: rule.transitMinutes ? Number(rule.transitMinutes) : undefined
        }))
        .filter((rule) => rule.days.length && rule.departures.length);
      if (rules.length) methods.push({ type: 'runs', rules });
    }

    if (this.pickupEnabled) {
      const rules = this.pickupRules
        .map((rule) => ({
          days: [...rule.days].sort(),
          from: rule.from || undefined,
          to: rule.to || undefined
        }))
        .filter((rule) => rule.days.length || rule.from || rule.to);
      methods.push({
        type: 'pickup',
        ...(rules.length ? { rules } : {}),
        ...(this.pickupTravelMinutes != null
          ? { travelMinutes: Number(this.pickupTravelMinutes) }
          : {}),
        ...(this.pickupHandlingMinutes != null
          ? { handlingMinutes: Number(this.pickupHandlingMinutes) }
          : {}),
        ...(this.pickupDistanceKm != null ? { distanceKm: Number(this.pickupDistanceKm) } : {}),
        ...(this.pickupAddress.trim() ? { address: this.pickupAddress.trim() } : {})
      });
    }

    const note = this.deliveryNote.trim();
    if (!methods.length && !note) return null;

    const delivery: ProviderWarehouseDelivery = { methods };
    if (note) delivery.note = note;
    return delivery;
  }

  async selectReceiver(id: number) {
    if (!this.loading && !this.saving && !this.switching && this.wh) {
      await this.changeContext(this.wh.id, id);
    }
  }

  routeSummary(id: number): string {
    const delivery =
      id === this.receiverWarehouseId && this.pairReady
        ? this.buildDeliveryPayload()
        : this.wh?.deliveryRoutes?.find((r) => r.receiverWarehouseId === id)?.delivery;
    return summarizeDelivery(delivery).join(' · ') || 'Не настроено';
  }

  copyRoute() {
    if (!this.canEdit || !this.copyReceiverId || this.copyReceiverId === this.receiverWarehouseId)
      return;
    const source = this.wh?.deliveryRoutes?.find(
      (r) => r.receiverWarehouseId === this.copyReceiverId
    )?.delivery;
    if (!source) {
      this.notificationService.showError('У выбранного склада нет настроек доставки');
      return;
    }
    this.loadDeliveryDraft(JSON.parse(JSON.stringify(source)));
    this.notificationService.notice('Настройки скопированы в форму. Проверьте время и сохраните.');
  }

  get validationError(): string {
    const validTime = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
    const optionalTime = (v: string) => !v || validTime(v);
    const range = (a: string, b: string) =>
      optionalTime(a) && optionalTime(b) && (!a || !b || a <= b);
    const number = (v: number | null, max: number) =>
      v == null || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= max);
    if (
      this.ownEnabled &&
      (!this.ownRules.length ||
        this.ownRules.some(
          (r) => !r.days.length || !validTime(r.cutoff) || !range(r.arrivalFrom, r.arrivalTo)
        ))
    )
      return 'Доставка: укажите дни заказа, время заказа и корректный интервал прибытия.';
    if (
      this.carrierEnabled &&
      (!this.carrierRules.length ||
        this.carrierRules.some(
          (r) => !r.days.length || !validTime(r.cutoff) || !number(r.transitDays, 30)
        ))
    )
      return 'Перевозчик: проверьте дни, время заказа и срок в пути.';
    if (
      this.runsEnabled &&
      (!this.runsRules.length ||
        this.runsRules.some(
          (r) =>
            !r.days.length ||
            !r.departures.length ||
            r.departures.some(
              (d) =>
                !validTime(d) ||
                (r.cutoff && r.cutoff > d) ||
                (!r.arrivalDayOffset &&
                  (r.arrivalFrom || r.arrivalTo) &&
                  (r.arrivalFrom || r.arrivalTo) < d)
            ) ||
            !optionalTime(r.cutoff) ||
            !range(r.arrivalFrom, r.arrivalTo) ||
            !number(r.transitMinutes, 1440)
        ))
    )
      return 'Рейс: проверьте дни, время заказа до отправления и прибытие после отправления.';
    if (
      this.pickupEnabled &&
      (this.pickupRules.some((r) => !range(r.from, r.to) || (r.from && r.to && r.from === r.to)) ||
        !number(this.pickupTravelMinutes, 1440) ||
        !number(this.pickupHandlingMinutes, 1440) ||
        !number(this.pickupDistanceKm, 2000) ||
        [this.pickupTravelMinutes, this.pickupHandlingMinutes].some(
          (v) => v != null && !Number.isInteger(Number(v))
        ))
    )
      return 'Самовывоз: проверьте часы работы, целые минуты дороги и выдачи, расстояние.';
    return '';
  }

  async save() {
    if (!this.wh || !this.receiverWarehouseId || !this.pairReady || this.loading || this.saving) {
      return false;
    }
    if (this.validationError) {
      this.notificationService.showError(this.validationError);
      return false;
    }
    this.saving = true;
    try {
      const response = await firstValueFrom(
        this.providerWarehouseService.updateDelivery(
          this.wh.id,
          this.receiverWarehouseId,
          this.buildDeliveryPayload()
        )
      );
      if (this.destroyed) return false;
      this.wh.deliveryRoutes = [
        ...(this.wh.deliveryRoutes || []).filter(
          (r) => r.receiverWarehouseId !== this.receiverWarehouseId
        ),
        { receiverWarehouseId: this.receiverWarehouseId!, delivery: response.delivery }
      ];
      this.loadDeliveryDraft(response.delivery);
      this.savedDraft = this.draftSnapshot();
      this.notificationService.notice('Доставка сохранена!');
      return true;
    } catch {
      if (!this.destroyed)
        this.notificationService.showError(
          'Не удалось сохранить доставку. Изменения оставлены в форме.'
        );
      return false;
    } finally {
      this.saving = false;
    }
  }
}
